import {
  setSession,
  setUser,
  getToken,
  type SessionUser,
} from './session.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export async function loginWithGoogle(credential: string): Promise<SessionUser> {
  const res = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) throw new Error(await readError(res));
  const data = (await res.json()) as { token: string; user: SessionUser };
  setSession(data.token, data.user);
  return data.user;
}

// Re-fetch the current account (status may have changed after admin approval).
export async function fetchMe(): Promise<SessionUser> {
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(await readError(res));
  const user = (await res.json()) as SessionUser;
  setUser(user);
  return user;
}

function authHeader(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readError(res: Response): Promise<string> {
  try {
    const json = (await res.json()) as { error?: string };
    return typeof json.error === 'string' ? json.error : `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

// --- Admin API ------------------------------------------------------------

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  avatarUrl: string | null;
  rating: number;
  role: string;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  loginCount: number;
}

export interface ActivityEvent {
  id?: string;
  type: string;
  at: string;
  roomId: string | null;
  ip: string | null;
  userAgent: string | null;
  meta?: string | null;
  user?: { id: string; username: string; email: string };
}

export interface AdminMatchPlayer {
  seat: number;
  userId: string | null;
  displayName: string;
  isBot: boolean;
  finishPlacement: number | null;
  finalTitle: string | null;
  ratingDelta: number | null;
}

export interface AdminMatch {
  id: string;
  mode: string;
  startedAt: string;
  endedAt: string | null;
  rounds: number;
  revolutions: number;
  players: AdminMatchPlayer[];
}

export interface AdminGameLog {
  seq: number;
  text: string;
  createdAt: string;
}

export interface RankingRow extends AdminUser {
  rank: number;
  games: number;
  wins: number;
}

export interface UserMatchRow {
  matchId: string;
  mode: string;
  startedAt: string;
  endedAt: string | null;
  rounds: number;
  seat: number;
  finishPlacement: number | null;
  finalTitle: string | null;
  ratingDelta: number | null;
}

export interface UserMatchHistory {
  user: { id: string; username: string; email: string; rating: number };
  stats: {
    games: number;
    wins: number;
    avgPlacement: number | null;
    rating: number;
  };
  matches: UserMatchRow[];
}

export interface PublicRankingRow {
  rank: number;
  id: string;
  username: string;
  avatarUrl: string | null;
  rating: number;
  games: number;
  wins: number;
}

// Hall of fame — readable by any approved user.
export async function publicRankings(): Promise<{
  rankings: PublicRankingRow[];
}> {
  const res = await fetch(`${API_BASE}/rankings`, { headers: authHeader() });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as { rankings: PublicRankingRow[] };
}

export interface PlacementStat {
  placement: number; // 0-indexed (0 = 1위)
  count: number;
  rate: number;
}

export interface PublicUserStats {
  user: {
    id: string;
    username: string;
    avatarUrl: string | null;
    rating: number;
  };
  games: number;
  wins: number;
  winRate: number;
  avgPlacement: number | null;
  bestPlacement: number | null;
  placements: PlacementStat[];
  titles: { title: string; count: number }[];
}

export async function publicUserStats(id: string): Promise<PublicUserStats> {
  const res = await fetch(`${API_BASE}/rankings/${id}`, {
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as PublicUserStats;
}

async function adminGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/admin${path}`, { headers: authHeader() });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as T;
}

async function adminPost<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/admin${path}`, {
    method: 'POST',
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as T;
}

export function adminListUsers(
  status: 'pending' | 'all' = 'all',
): Promise<{ users: AdminUser[] }> {
  return adminGet(`/users?status=${status}`);
}

export function adminApprove(id: string): Promise<unknown> {
  return adminPost(`/users/${id}/approve`);
}

export function adminReject(id: string): Promise<unknown> {
  return adminPost(`/users/${id}/reject`);
}

export function adminListMatches(
  limit = 100,
): Promise<{ matches: AdminMatch[] }> {
  return adminGet(`/matches?limit=${limit}`);
}

export function adminMatchLogs(
  matchId: string,
): Promise<{ logs: AdminGameLog[] }> {
  return adminGet(`/matches/${matchId}/logs`);
}

export function adminUserMatches(userId: string): Promise<UserMatchHistory> {
  return adminGet(`/users/${userId}/matches`);
}

export function adminRankings(): Promise<{ rankings: RankingRow[] }> {
  return adminGet('/rankings');
}

export function adminUserActivity(
  userId: string,
): Promise<{ events: ActivityEvent[] }> {
  return adminGet(`/users/${userId}/activity`);
}

export function adminActivity(
  limit = 200,
): Promise<{ events: ActivityEvent[] }> {
  return adminGet(`/activity?limit=${limit}`);
}
